/**
 * Physician Notes & Scheduling Step Component
 * 
 * Fourth and final step in the MedPass workflow - handles clinical documentation,
 * physician notes, and medication scheduling information.
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Form,
    Input,
    Select,
    Button,
    Alert,
    Space,
    Table,
    Modal,
    message,
    Tabs,
    DatePicker,
    TimePicker,
    Checkbox,
    Tag,
    Divider
} from 'antd';
import {
    FileTextOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    ClockCircleOutlined,
    UserOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import moment from 'moment';

import medpassService from '../../../services/medpassService';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const PhysicianNotesSchedulingStep = ({ workflow, onSave, onNext, onPrev, loading }) => {
    const [form] = Form.useForm();
    const [physicianNotes, setPhysicianNotes] = useState([]);
    const [schedulingInfo, setSchedulingInfo] = useState(null);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [schedulingTemplates, setSchedulingTemplates] = useState([]);

    // Note types
    const noteTypes = [
        { value: 'assessment', label: 'Clinical Assessment' },
        { value: 'plan', label: 'Treatment Plan' },
        { value: 'progress', label: 'Progress Note' },
        { value: 'medication_review', label: 'Medication Review' },
        { value: 'safety_review', label: 'Safety Review' },
        { value: 'monitoring_plan', label: 'Monitoring Plan' },
        { value: 'discharge_planning', label: 'Discharge Planning' },
        { value: 'other', label: 'Other' }
    ];

    // Administration methods - using new enum values
    const administrationMethods = {
        'direct': 'Direct Administration',
        'injection': 'Injection',
        'iv_push': 'IV Push',
        'iv_drip': 'IV Drip',
        'feeding_tube': 'Feeding Tube',
        'nebulizer': 'Nebulizer',
        'inhaler': 'Inhaler'
    };

    // Initialize with existing data
    useEffect(() => {
        if (workflow) {
            setPhysicianNotes(workflow.physician_notes || []);
            setSchedulingInfo(workflow.scheduling_info);
            
            if (workflow.scheduling_info) {
                form.setFieldsValue({
                    start_date: workflow.scheduling_info.start_date ? moment(workflow.scheduling_info.start_date) : null,
                    end_date: workflow.scheduling_info.end_date ? moment(workflow.scheduling_info.end_date) : null,
                    administration_times: workflow.scheduling_info.administration_times || [],
                    frequency: workflow.scheduling_info.frequency,
                    administration_method: workflow.scheduling_info.administration_method,
                    administrator_name: workflow.scheduling_info.administrator_name,
                    timing_instructions: workflow.scheduling_info.timing_instructions,
                    food_instructions: workflow.scheduling_info.food_instructions,
                    activity_restrictions: workflow.scheduling_info.activity_restrictions
                });
            }
        }
    }, [workflow, form]);

    // Load scheduling templates
    useEffect(() => {
        loadSchedulingTemplates();
    }, []);

    const loadSchedulingTemplates = async () => {
        try {
            const result = await medpassService.getSchedulingTemplates();
            if (result.success) {
                setSchedulingTemplates(result.data);
            }
        } catch (error) {
            console.error('Error loading scheduling templates:', error);
        }
    };

    const addPhysicianNote = () => {
        setEditingNote(null);
        form.resetFields();
        setShowNoteModal(true);
    };

    const editPhysicianNote = (note, index) => {
        setEditingNote({ ...note, index });
        
        form.setFieldsValue({
            note_type: note.note_type,
            content: note.content,
            diagnosis_codes: note.diagnosis_codes || [],
            clinical_indication: note.clinical_indication,
            treatment_goals: note.treatment_goals || [],
            physician_name: note.physician_name,
            physician_license: note.physician_license
        });
        
        setShowNoteModal(true);
    };

    const savePhysicianNote = async () => {
        try {
            const values = await form.validateFields();
            
            const newNote = {
                note_id: editingNote?.note_id || `note_${Date.now()}`,
                note_type: values.note_type,
                content: values.content,
                diagnosis_codes: values.diagnosis_codes || [],
                clinical_indication: values.clinical_indication,
                treatment_goals: values.treatment_goals || [],
                physician_id: 'current_user', // Should come from auth context
                physician_name: values.physician_name,
                physician_license: values.physician_license,
                created_at: editingNote?.created_at || new Date().toISOString(),
                last_modified: new Date().toISOString()
            };

            if (editingNote) {
                const updated = [...physicianNotes];
                updated[editingNote.index] = newNote;
                setPhysicianNotes(updated);
            } else {
                setPhysicianNotes([...physicianNotes, newNote]);
            }

            setShowNoteModal(false);
            form.resetFields();
            message.success('Physician note saved successfully');
        } catch (error) {
            message.error('Please complete all required fields');
        }
    };

    const removePhysicianNote = (index) => {
        setPhysicianNotes(physicianNotes.filter((_, i) => i !== index));
        message.success('Physician note removed');
    };

    const applySchedulingTemplate = (template) => {
        form.setFieldsValue({
            administration_times: template.administration_times,
            frequency: template.frequency,
            administration_method: template.administration_method,
            timing_instructions: template.timing_instructions,
            food_instructions: template.food_instructions
        });
        message.success('Scheduling template applied');
    };

    const saveScheduling = async () => {
        try {
            const values = await form.validateFields([
                'start_date',
                'administration_times',
                'frequency',
                'administration_method'
            ]);

            const scheduling = {
                schedule_id: schedulingInfo?.schedule_id || `sched_${Date.now()}`,
                start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
                end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
                administration_times: values.administration_times || [],
                frequency: values.frequency,
                administration_method: values.administration_method,
                administrator_name: values.administrator_name,
                timing_instructions: values.timing_instructions,
                food_instructions: values.food_instructions,
                activity_restrictions: values.activity_restrictions,
                monitoring_schedule: {},
                follow_up_schedule: {}
            };

            setSchedulingInfo(scheduling);
            message.success('Scheduling information saved');
        } catch (error) {
            message.error('Please complete all required scheduling fields');
        }
    };

    // Table columns for physician notes
    const noteColumns = [
        {
            title: 'Type',
            dataIndex: 'note_type',
            key: 'note_type',
            render: (type) => {
                const noteType = noteTypes.find(nt => nt.value === type);
                return <Tag color="blue">{noteType?.label || type}</Tag>;
            }
        },
        {
            title: 'Content',
            dataIndex: 'content',
            key: 'content',
            ellipsis: true,
            render: (content) => content.substring(0, 100) + (content.length > 100 ? '...' : '')
        },
        {
            title: 'Physician',
            dataIndex: 'physician_name',
            key: 'physician_name'
        },
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => moment(date).format('DD/MM/YYYY hh:mm A')
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record, index) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<EditOutlined />}
                        onClick={() => editPhysicianNote(record, index)}
                        size="small"
                    >
                        Edit
                    </Button>
                    <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />}
                        onClick={() => removePhysicianNote(index)}
                        size="small"
                    >
                        Remove
                    </Button>
                </Space>
            )
        }
    ];

    // Save step data
    const handleSave = async () => {
        if (physicianNotes.length === 0) {
            message.error('Please add at least one physician note');
            return false;
        }

        if (!schedulingInfo) {
            message.error('Please complete the scheduling information');
            return false;
        }

        const updateData = {
            physician_notes: physicianNotes,
            scheduling_info: schedulingInfo
        };

        const success = await onSave(updateData, 'clinical_documentation');
        if (success) {
            message.success('Physician notes and scheduling saved successfully');
            return true;
        }
        return false;
    };

    const handleNext = async () => {
        const saved = await handleSave();
        if (saved && onNext) {
            // Add a small delay to ensure workflow state is updated
            await new Promise(resolve => setTimeout(resolve, 100));
            await onNext();
        }
    };

    return (
        <div className="physician-notes-scheduling-step">
            <Card 
                title={
                    <Space>
                        <FileTextOutlined />
                        Physician Notes & Scheduling
                    </Space>
                }
            >
                <Tabs defaultActiveKey="notes" type="card">
                    <TabPane 
                        tab={`Physician Notes (${physicianNotes.length})`} 
                        key="notes"
                    >
                        <div style={{ marginBottom: 16 }}>
                            <Alert
                                message="Clinical Documentation"
                                description="Add physician notes, clinical assessments, and treatment plans for this medication regimen."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                            
                            <Button 
                                type="primary" 
                                icon={<PlusOutlined />}
                                onClick={addPhysicianNote}
                            >
                                Add Physician Note
                            </Button>
                        </div>

                        <Table
                            dataSource={physicianNotes}
                            columns={noteColumns}
                            rowKey="note_id"
                            pagination={false}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <FileTextOutlined style={{ fontSize: '24px', color: '#ccc' }} />
                                        <div style={{ marginTop: 8, color: '#999' }}>
                                            No physician notes added
                                        </div>
                                        <Button 
                                            type="primary" 
                                            icon={<PlusOutlined />}
                                            onClick={addPhysicianNote}
                                            style={{ marginTop: 8 }}
                                        >
                                            Add First Note
                                        </Button>
                                    </div>
                                )
                            }}
                        />
                    </TabPane>

                    <TabPane 
                        tab="Medication Scheduling" 
                        key="scheduling"
                    >
                        <Alert
                            message="Medication Administration Schedule"
                            description="Define when and how medications should be administered, including timing, frequency, and special instructions."
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={saveScheduling}
                        >
                            <Row gutter={[16, 0]}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Start Date"
                                        name="start_date"
                                        rules={[{ required: true, message: 'Start date is required' }]}
                                    >
                                        <DatePicker 
                                            style={{ width: '100%' }}
                                            placeholder="Select start date"
                                        />
                                    </Form.Item>
                                </Col>
                                
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="End Date (Optional)"
                                        name="end_date"
                                    >
                                        <DatePicker 
                                            style={{ width: '100%' }}
                                            placeholder="Select end date"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Frequency"
                                        name="frequency"
                                        rules={[{ required: true, message: 'Frequency is required' }]}
                                    >
                                        <Select placeholder="Select frequency">
                                            <Option value="once_daily">Once Daily</Option>
                                            <Option value="twice_daily">Twice Daily</Option>
                                            <Option value="three_times_daily">Three Times Daily</Option>
                                            <Option value="four_times_daily">Four Times Daily</Option>
                                            <Option value="with_meals">With Meals</Option>
                                            <Option value="as_needed">As Needed</Option>
                                            <Option value="every_4_hours">Every 4 Hours</Option>
                                            <Option value="every_6_hours">Every 6 Hours</Option>
                                            <Option value="every_8_hours">Every 8 Hours</Option>
                                            <Option value="every_12_hours">Every 12 Hours</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Administration Method"
                                        name="administration_method"
                                        rules={[{ required: true, message: 'Administration method is required' }]}
                                    >
                                        <Select placeholder="Select administration method">
                                            {Object.entries(administrationMethods).map(([key, label]) => (
                                                <Option key={key} value={key}>{label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24}>
                                    <Form.Item
                                        label="Administration Times"
                                        name="administration_times"
                                        rules={[{ required: true, message: 'At least one administration time is required' }]}
                                    >
                                        <Select
                                            mode="tags"
                                            placeholder="Enter times (e.g., 08:00, 14:00, 20:00)"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Administrator Name"
                                        name="administrator_name"
                                    >
                                        <Input placeholder="Name of person administering medication" />
                                    </Form.Item>
                                </Col>

                                <Col xs={24}>
                                    <Form.Item
                                        label="Timing Instructions"
                                        name="timing_instructions"
                                    >
                                        <TextArea 
                                            placeholder="Special timing instructions (e.g., take 30 minutes before meals)"
                                            rows={2}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Food Instructions"
                                        name="food_instructions"
                                    >
                                        <TextArea 
                                            placeholder="Food-related instructions (e.g., take with food, avoid dairy)"
                                            rows={2}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Activity Restrictions"
                                        name="activity_restrictions"
                                    >
                                        <TextArea 
                                            placeholder="Any activity restrictions (e.g., avoid driving, limit physical activity)"
                                            rows={2}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Button 
                                type="dashed" 
                                onClick={saveScheduling}
                                style={{ marginBottom: 16 }}
                            >
                                Save Scheduling Information
                            </Button>
                        </Form>

                        {/* Scheduling Templates */}
                        {schedulingTemplates.length > 0 && (
                            <div>
                                <Divider />
                                <h4>Quick Scheduling Templates:</h4>
                                <Row gutter={[8, 8]}>
                                    {schedulingTemplates.map((template, index) => (
                                        <Col key={index}>
                                            <Button 
                                                size="small" 
                                                type="dashed"
                                                onClick={() => applySchedulingTemplate(template)}
                                            >
                                                {template.name}
                                            </Button>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                    </TabPane>
                </Tabs>

                {/* Action Buttons */}
                <Row justify="space-between" style={{ marginTop: 24 }}>
                    <Col>
                        <Button onClick={onPrev}>
                            Previous
                        </Button>
                    </Col>
                    
                    <Col>
                        <Button type="primary" onClick={handleNext} loading={loading}>
                            Complete Workflow
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Physician Note Modal */}
            <Modal
                title={`${editingNote ? 'Edit' : 'Add'} Physician Note`}
                open={showNoteModal}
                onOk={savePhysicianNote}
                onCancel={() => {
                    setShowNoteModal(false);
                    form.resetFields();
                }}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        label="Note Type"
                        name="note_type"
                        rules={[{ required: true, message: 'Note type is required' }]}
                    >
                        <Select placeholder="Select note type">
                            {noteTypes.map(type => (
                                <Option key={type.value} value={type.value}>
                                    {type.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Clinical Note Content"
                        name="content"
                        rules={[{ required: true, message: 'Note content is required' }]}
                    >
                        <TextArea 
                            placeholder="Enter detailed clinical notes, assessment, or treatment plan"
                            rows={6}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Diagnosis Codes (ICD-10)"
                                name="diagnosis_codes"
                            >
                                <Select
                                    mode="tags"
                                    placeholder="Enter ICD-10 diagnosis codes"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Clinical Indication"
                                name="clinical_indication"
                            >
                                <Input placeholder="Primary indication for medication" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Treatment Goals"
                        name="treatment_goals"
                    >
                        <Select
                            mode="tags"
                            placeholder="Enter treatment goals (press Enter to add)"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Physician Name"
                                name="physician_name"
                                rules={[{ required: true, message: 'Physician name is required' }]}
                            >
                                <Input placeholder="Attending physician name" />
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="License Number"
                                name="physician_license"
                                rules={[{ required: true, message: 'License number is required' }]}
                            >
                                <Input placeholder="Medical license number" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default PhysicianNotesSchedulingStep;